// frontend/src/features/metrics/services/metricsService.ts
import { ROUTE_METRICS } from '../../../config/routes/routeConfig';
interface ApiCallData {
	count: number;
	totalDuration: number;
}

interface Metrics {
	performance: Map<string, number>;
	pageViews: Map<string, number>;
	errors: Map<string, number>;
	apiCalls: Map<string, ApiCallData>;
}

class MetricsService {
	private readonly metrics: Metrics;
	private readonly metricsInterval: number;
	private readonly retryTimeout: number;
	private readonly maxRetries: number;
	private currentRetries: number;
	private readonly metricsUrl: string;
	private intervalId: NodeJS.Timeout | null = null;

	constructor() {
		this.metrics = {
			performance: new Map(),
			pageViews: new Map(),
			errors: new Map(),
			apiCalls: new Map(),
		};

		this.metricsInterval = 10000; // 10 segundos
		this.retryTimeout = 5000; // 5 segundos de espera entre reintentos
		this.maxRetries = 3;
		this.currentRetries = 0;

		// Base URL para métricas
		this.metricsUrl = ROUTE_METRICS;

		this.startMetricsCollection();
	}

	startMetricsCollection(): void {
		this.intervalId ??= setInterval(() => {
			this.sendMetrics();
		}, this.metricsInterval);
	}

	stopMetricsCollection(): void {
		if (this.intervalId) {
			clearInterval(this.intervalId);
			this.intervalId = null;
		}
	}

	recordMetric(type: keyof Metrics, key: string, value: number): void {
		if (!this.metrics[type]) return;

		switch (type) {
			case 'performance':
				this.metrics.performance.set(key, value);
				break;
			case 'pageViews': {
				const viewCount = this.metrics.pageViews.get(key) || 0;
				this.metrics.pageViews.set(key, viewCount + 1);
				break;
			}
			case 'errors': {
				const errorCount = this.metrics.errors.get(key) || 0;
				this.metrics.errors.set(key, errorCount + 1);
				break;
			}
			case 'apiCalls': {
				const calls = this.metrics.apiCalls.get(key) || {
					count: 0,
					totalDuration: 0,
				};
				this.metrics.apiCalls.set(key, {
					count: calls.count + 1,
					totalDuration: calls.totalDuration + (value || 0),
				});
				break;
			}
		}
	}

	formatMetricsForPrometheus(): string {
		const timestamp = Math.floor(Date.now() / 1000);
		const lines: string[] = [];

		// Definiciones de tipos
		lines.push(
			'# HELP frontend_performance_metrics Frontend performance metrics',
			'# TYPE frontend_performance_metrics gauge',
			'# HELP frontend_page_views_total Total number of page views',
			'# TYPE frontend_page_views_total counter',
			'# HELP frontend_errors_total Total number of frontend errors',
			'# TYPE frontend_errors_total counter',
			'# HELP frontend_api_calls_total Total number of API calls',
			'# TYPE frontend_api_calls_total counter',
			'# HELP frontend_api_duration_seconds Duration of API calls',
			'# TYPE frontend_api_duration_seconds gauge'
		);

		// Performance metrics
		for (const [metric, value] of this.metrics.performance) {
			lines.push(
				`frontend_performance_metrics{metric="${metric}"} ${value} ${timestamp}`
			);
		}

		// Page views
		for (const [page, count] of this.metrics.pageViews) {
			lines.push(
				`frontend_page_views_total{page="${page}"} ${count} ${timestamp}`
			);
		}

		// Errors
		for (const [error, count] of this.metrics.errors) {
			const parts = error.split('_');
			const errorType = parts[0] || 'unknown';
			const context = parts[1] || 'unknown';
			lines.push(
				`frontend_errors_total{type="${errorType}",context="${context}"} ${count} ${timestamp}`
			);
		}

		// API calls
		for (const [key, data] of this.metrics.apiCalls) {
			const apiParts = key.split('_');
			const method = apiParts[0] || 'unknown';
			const endpoint = apiParts[1] || 'unknown';
			const status = apiParts[2] || 'unknown';
			lines.push(
				`frontend_api_calls_total{method="${method}",endpoint="${endpoint}",status="${status}"} ${data.count} ${timestamp}`,
				`frontend_api_duration_seconds{method="${method}",endpoint="${endpoint}",status="${status}"} ${
					data.totalDuration / 1000
				} ${timestamp}`
			);
		}

		return lines.join('\n');
	}

	async sendMetrics(retry: boolean = false): Promise<void> {
		if (
			this.metrics.performance.size === 0 &&
			this.metrics.pageViews.size === 0 &&
			this.metrics.errors.size === 0 &&
			this.metrics.apiCalls.size === 0
		) {
			return;
		}

		try {
			const metricsData = this.formatMetricsForPrometheus();

			const response = await fetch(this.metricsUrl, {
				method: 'POST',
				headers: {
					'Content-Type': 'text/plain',
					Accept: 'text/plain',
				},
				body: metricsData,
				credentials: 'include',
			});

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			this.clearMetrics();
			this.currentRetries = 0;
		} catch {
			// Retry on failure if not already retrying and within retry limit
			if (!retry && this.currentRetries < this.maxRetries) {
				this.currentRetries++;
				setTimeout(() => {
					this.sendMetrics(true);
				}, this.retryTimeout);
			}
			// If maxRetries reached, silently drop metrics to avoid blocking
		}
	}

	clearMetrics(): void {
		for (const metric of Object.values(this.metrics)) metric.clear();
	}

	// Métodos de conveniencia
	recordPerformanceMetric(metric: string, value: number): void {
		this.recordMetric('performance', metric, value);
	}

	recordPageView(page: string): void {
		this.recordMetric('pageViews', page, 1);
	}

	recordError(error: Error, context: string): void {
		this.recordMetric('errors', `${error.name}_${context}`, 1);
	}

	recordApiCall(endpoint: string, method: string, status: string, duration: number): void {
		this.recordMetric('apiCalls', `${method}_${endpoint}_${status}`, duration);
	}
}

export const metricsService = new MetricsService();
